import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';

export const usePopularTerms = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  // Função para buscar termos populares
  const getPopularTerms = async (limit: number = 10) => {
    try {
      // Se não há usuário logado, usar apenas o apikey
      if (!user) {
        const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/get_popular_terms`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY || ''
          },
          body: JSON.stringify({ limit })
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        return response.json();
      }

      // Se há usuário logado, obter o token da sessão atual
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      
      if (!token) {
        console.warn('No access token found, using anonymous request');
        const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/get_popular_terms`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY || ''
          },
          body: JSON.stringify({ limit })
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        return response.json();
      }

      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/get_popular_terms`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY || ''
        },
        body: JSON.stringify({ limit })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return response.json();
    } catch (error) {
      console.error('Get popular terms error:', error);
      throw error;
    }
  };

  const { data: popularTerms = [], isLoading, refetch } = useQuery({
    queryKey: ['popularTerms'],
    queryFn: async () => {
      const result = await getPopularTerms(10);
      return result?.terms || [];
    },
    staleTime: 5 * 60 * 1000, // Reduzido para 5 minutos
    refetchOnWindowFocus: false,
    notifyOnChangeProps: ['data', 'isLoading'], // Forçar notificação de mudanças
  });

  const addTermLocally = useCallback((newTerm: string) => {
    queryClient.setQueryData(['popularTerms'], (oldData: string[] | undefined) => {
      if (!oldData) {
        return [newTerm];
      }
      
      // Se o termo já existe, movê-lo para o início
      const filtered = oldData.filter(term => term !== newTerm);
      const newData = [newTerm, ...filtered].slice(0, 10); // Manter apenas os 10 primeiros
      return newData;
    });
  }, [queryClient]);

  return {
    popularTerms,
    isLoading,
    addTermLocally,
    refetch
  };
}; 